import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, MessageSquarePlus, TriangleAlert } from "lucide-react";
import { lazy, Suspense, useCallback } from "react";
import { ArtifactKindIcon } from "@/components/artifacts/artifact-kind.tsx";
import { MilkdownViewer } from "@/components/editor/milkdown-viewer/index.tsx";
import { Button } from "@/components/ui/button.tsx";
import { m } from "@/paraglide/messages.js";
import {
	resolveArtifactByIdQueryOptions,
	spaceArtifactMarkdownQueryOptions,
} from "@/services/artifacts-service.ts";
import { useArtifactUpdateListener } from "@/utils/artifact-sync.ts";
import { requireActiveOnboardedUser } from "@/utils/route-auth-guard.ts";

// Lazy for the same bundle-splitting reason as the in-panel viewers: these pull
// heavy deps (Recharts, the canvas runtime) that stay out of the route bundle
// until an artifact of that kind is actually opened here.
const DeckViewer = lazy(() =>
	import("@/components/presentation/deck-viewer.tsx").then((mod) => ({
		default: mod.DeckViewer,
	})),
);
const CanvasViewer = lazy(() =>
	import("@/components/canvas/canvas-viewer.tsx").then((mod) => ({
		default: mod.CanvasViewer,
	})),
);
const DashboardViewer = lazy(() =>
	import("@/components/bi-dashboard/dashboard-viewer.tsx").then((mod) => ({
		default: mod.DashboardViewer,
	})),
);

/**
 * Standalone full-screen artifact viewer at `/open-artifact/$artifactId`.
 *
 * Two jobs: it is the target of the artifact "copy link" (previously a dead
 * URL) AND the pop-out target of the in-chat artifact panel — a separate,
 * chrome-free window so a big canvas/dashboard gets the whole viewport and the
 * chat tab reclaims its width. Deliberately top-level (NOT under
 * `_authenticated`) so it renders without the app sidebar; it runs its own auth
 * guard. Only an `artifactId` is needed — the cross-space resolver returns the
 * artifact's real `spaceId` + `kind`, and every downstream read re-authorizes
 * server-side (`authenticateForArtifact`).
 */
export const Route = createFileRoute("/open-artifact/$artifactId")({
	beforeLoad: ({ context, location }) =>
		requireActiveOnboardedUser({ context, location }),
	component: OpenArtifactPage,
	// A failed resolve (network / 5xx) would otherwise bubble to a blank screen
	// in this chrome-free window; show a friendly full-screen fallback instead.
	errorComponent: () => (
		<div className="flex h-svh flex-col items-center justify-center gap-2 p-6 text-center">
			<TriangleAlert className="size-8 text-destructive" />
			<p className="font-medium text-foreground">
				{m.artifacts_viewer_title()}
			</p>
			<p className="max-w-md text-sm text-muted-foreground">
				{m.artifact_not_found_or_no_access()}
			</p>
		</div>
	),
});

function LoadingState() {
	return (
		<div className="flex h-full flex-col items-center justify-center gap-4">
			<Loader2 className="h-8 w-8 animate-spin" />
			<p className="text-sm text-muted-foreground">
				{m.artifacts_loading_artifact()}
			</p>
		</div>
	);
}

function OpenArtifactPage() {
	const { artifactId } = Route.useParams();
	const navigate = useNavigate();
	const { data } = useSuspenseQuery(
		resolveArtifactByIdQueryOptions(artifactId),
	);
	const artifact = data.artifacts[0];

	// Live-sync: the chat tab broadcasts an artifact id when the LLM edits it;
	// re-fetch this window's viewer query so a popped-out view stays current.
	// (Hooks must run before the early return below.)
	const queryClient = useQueryClient();
	const viewerQueryPath =
		artifact?.kind === "presentation"
			? "/api/spaces/{spaceId}/artifacts/{artifactId}/deck"
			: artifact?.kind === "canvas"
				? "/api/spaces/{spaceId}/artifacts/{artifactId}/canvas"
				: artifact?.kind === "bi_dashboard"
					? "/api/spaces/{spaceId}/artifacts/{artifactId}/dashboard-data"
					: "/api/spaces/{spaceId}/artifacts/{artifactId}/markdown";
	const onArtifactUpdated = useCallback(
		(id: string) => {
			if (id !== artifactId) {
				return;
			}
			queryClient.invalidateQueries({ queryKey: ["get", viewerQueryPath] });
		},
		[artifactId, viewerQueryPath, queryClient],
	);
	useArtifactUpdateListener(onArtifactUpdated);

	if (!artifact) {
		return (
			<div className="flex h-svh flex-col items-center justify-center gap-2 p-6 text-center">
				<p className="font-medium text-foreground">
					{m.artifacts_viewer_title()}
				</p>
				<p className="max-w-md text-sm text-muted-foreground">
					{m.artifact_not_found_or_no_access()}
				</p>
			</div>
		);
	}

	const { spaceId, name, kind } = artifact;

	return (
		<div className="flex h-svh flex-col bg-background">
			<header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
				<ArtifactKindIcon kind={kind} className="size-4 shrink-0" />
				<span className="min-w-0 flex-1 truncate font-medium">{name}</span>
				<Button
					variant="outline"
					size="sm"
					className="gap-2"
					onClick={() => navigate({ to: "/home", search: { artifactId } })}
				>
					<MessageSquarePlus className="h-4 w-4" />
					{m.artifacts_open_in_chat()}
				</Button>
			</header>
			<div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/50 p-3">
				<Suspense fallback={<LoadingState />}>
					{kind === "presentation" ? (
						<DeckViewer spaceId={spaceId} artifactId={artifactId} name={name} />
					) : kind === "canvas" ? (
						<CanvasViewer
							spaceId={spaceId}
							artifactId={artifactId}
							name={name}
						/>
					) : kind === "bi_dashboard" ? (
						<DashboardViewer spaceId={spaceId} artifactId={artifactId} />
					) : (
						<DocumentView
							spaceId={spaceId}
							artifactId={artifactId}
							name={name}
						/>
					)}
				</Suspense>
			</div>
		</div>
	);
}

function DocumentView({
	spaceId,
	artifactId,
	name,
}: {
	spaceId: string;
	artifactId: string;
	name: string;
}) {
	const { data } = useSuspenseQuery(
		spaceArtifactMarkdownQueryOptions(spaceId, artifactId),
	);
	return (
		<div className="min-h-0 flex-1 overflow-y-auto">
			<MilkdownViewer
				markdown={data.markdown}
				spaceId={spaceId}
				artifactId={artifactId}
				documentTitle={name}
			/>
		</div>
	);
}
